/**
 * Card art loading shared by every card surface (batch 3 #27): a failed source moves on to the next one at once (the
 * R2 mirror, then Riot's CDN); the last one gets a bounded number of retries with backoff; then a named placeholder.
 * A URL that exhausted its retries is remembered for the page, so the board, the inspector and the preview agree
 * (no surface keeps retrying what another gave up on), and "Retry artwork" clears it for every surface at once.
 */
import { useCallback, useEffect, useReducer, useRef, useSyncExternalStore } from 'react';

/** Retries of one source before moving to the next. */
export const ART_RETRIES = 2;
/** Backoff before retry n (0-based), in ms. */
export const ART_BACKOFF_MS: readonly number[] = [600, 1800];

/** Where loading a list of sources stands. */
export interface ArtLoadState {
  /** Index into the sources. */
  index: number;
  /** Retry of that source (0 = first try). */
  attempt: number;
  /** Waiting for the backoff before the next try. */
  waiting: boolean;
  /** The image loaded. */
  loaded: boolean;
}

/** What to do after a failed try: retry the same source after `delay` ms, or move on to `index`. */
export type ArtStep = { kind: 'retry'; attempt: number; delay: number } | { kind: 'next'; index: number };

/**
 * The step after source `index` of `count` failed on try `attempt` (pure). A failed source that has a fallback moves
 * on at once (a player waiting on a preview shouldn't sit through backoffs on a mirror that misses); only the last
 * source gets the bounded retries with backoff (QoL 2026-10-16).
 */
export function nextArtStep(index: number, attempt: number, retries = ART_RETRIES, count = index + 1): ArtStep {
  if (index < count - 1) return { kind: 'next', index: index + 1 };
  if (attempt < retries) {
    return { kind: 'retry', attempt: attempt + 1, delay: ART_BACKOFF_MS[attempt] ?? ART_BACKOFF_MS.at(-1) ?? 1000 };
  }
  return { kind: 'next', index: index + 1 };
}

/** The URL to request for a try: retries add a query so a cached failure is not reused. */
export function artAttemptUrl(src: string, attempt: number): string {
  if (attempt === 0) return src;
  return `${src}${src.includes('?') ? '&' : '?'}retry=${attempt}`;
}

// ── Page-wide memory of failed URLs ───────────────────────────────────────────────────────────────────
const failed = new Set<string>();
let epoch = 0;
const listeners = new Set<() => void>();

/** Remembers that `url` failed every retry (other surfaces skip it). */
export function markArtFailed(url: string): void {
  failed.add(url);
}

/** Whether `url` already failed every retry on this page. */
export function isArtFailed(url: string): boolean {
  return failed.has(url);
}

/** Forgets the failures of `urls` (all of them without an argument) and tells every surface to try again. */
export function retryArt(urls?: readonly string[]): void {
  if (urls) urls.forEach((u) => failed.delete(u));
  else failed.clear();
  epoch++;
  listeners.forEach((fn) => {
    fn();
  });
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

const getEpoch = () => epoch;

/** The first source at or after `from` that hasn't failed on this page. */
function firstLive(sources: readonly string[], from: number): number {
  let i = from;
  while (i < sources.length && failed.has(sources[i] as string)) i++;
  return i;
}

function initialState(sources: readonly string[]): ArtLoadState {
  return { index: firstLive(sources, 0), attempt: 0, waiting: false, loaded: false };
}

type Action =
  | { t: 'reset'; sources: readonly string[] }
  | { t: 'loaded' }
  | { t: 'wait'; attempt: number }
  | { t: 'go' }
  | { t: 'next'; sources: readonly string[]; index: number };

function reduce(s: ArtLoadState, a: Action): ArtLoadState {
  switch (a.t) {
    case 'reset':
      return initialState(a.sources);
    case 'loaded':
      return { ...s, loaded: true };
    case 'wait':
      return { ...s, attempt: a.attempt, waiting: true, loaded: false };
    case 'go':
      return { ...s, waiting: false };
    case 'next':
      return { index: firstLive(a.sources, a.index), attempt: 0, waiting: false, loaded: false };
  }
}

/** Card art for an `<img>`: what to request now and what happened. */
export interface CardArt {
  /** URL for the `<img>` now (`null` while waiting for a retry, or when there is nothing left to try). */
  src: string | null;
  /** A key that changes with every try (remounts the `<img>`). */
  tryKey: string;
  loaded: boolean;
  /** Every source failed (there were some): show the placeholder and offer "Retry artwork". */
  failed: boolean;
  /** No sources at all (tokens without art): the placeholder is the card's look, not a failure. */
  none: boolean;
  onLoad: () => void;
  onError: () => void;
  /** Tries every source again, on every surface showing them. */
  retry: () => void;
}

/** Loads `sources` with bounded retries and fallbacks (see the module header). */
export function useCardArt(sources: readonly string[]): CardArt {
  const key = sources.join('\n');
  const [state, dispatch] = useReducer(reduce, sources, initialState);
  const sourcesRef = useRef(sources);
  sourcesRef.current = sources;
  const stateRef = useRef(state);
  stateRef.current = state;
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearTimer = () => {
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = null;
  };

  // A different card: start over.
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    clearTimer();
    dispatch({ t: 'reset', sources: sourcesRef.current });
  }, [key]);
  useEffect(() => clearTimer, []);

  // "Retry artwork" anywhere on the page: surfaces that gave up start again.
  const currentEpoch = useSyncExternalStore(subscribe, getEpoch, getEpoch);
  const seenEpoch = useRef(currentEpoch);
  useEffect(() => {
    if (seenEpoch.current === currentEpoch) return;
    seenEpoch.current = currentEpoch;
    if (stateRef.current.index >= sourcesRef.current.length) {
      clearTimer();
      dispatch({ t: 'reset', sources: sourcesRef.current });
    }
  }, [currentEpoch]);

  const onLoad = useCallback(() => {
    dispatch({ t: 'loaded' });
  }, []);
  const onError = useCallback(() => {
    const s = stateRef.current;
    const list = sourcesRef.current;
    const step = nextArtStep(s.index, s.attempt, ART_RETRIES, list.length);
    if (step.kind === 'retry') {
      dispatch({ t: 'wait', attempt: step.attempt });
      clearTimer();
      timer.current = setTimeout(() => {
        timer.current = null;
        dispatch({ t: 'go' });
      }, step.delay);
      return;
    }
    const url = list[s.index];
    if (url !== undefined) markArtFailed(url);
    dispatch({ t: 'next', sources: list, index: step.index });
  }, []);
  const retry = useCallback(() => {
    retryArt(sourcesRef.current);
    clearTimer();
    dispatch({ t: 'reset', sources: sourcesRef.current });
  }, []);

  const base = sources[state.index];
  const exhausted = state.index >= sources.length;
  return {
    src: base !== undefined && !state.waiting ? artAttemptUrl(base, state.attempt) : null,
    tryKey: `${state.index}:${state.attempt}`,
    loaded: state.loaded,
    failed: sources.length > 0 && exhausted,
    none: sources.length === 0,
    onLoad,
    onError,
    retry,
  };
}
